import React, { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts";
import styles from "./BenchmarkScatterChart.module.scss";
import { formatBenchmarkChartValue } from "../../benchmarkMetricConfig";
import { buildInstitutionScatterPoints } from "../../benchmarkChartDerivations";

const scaleBubble = (size, min, max) => {
  if (max <= min) return 46;
  const t = (size - min) / (max - min);
  /* Map the h-index range onto a generous bubble size band. */
  return 28 + t * 52;
};

const BenchmarkScatterChart = ({ benchmarkRows, colorMap, exportMode = false }) => {
  const chartRef = useRef(null);
  const scatter = useMemo(
    () => buildInstitutionScatterPoints(benchmarkRows),
    [benchmarkRows]
  );
  const paddedDomain = useMemo(() => {
    if (!scatter.points.length) return null;

    const xs = scatter.points.map((p) => p.x);
    const ys = scatter.points.map((p) => p.y);
    const xMin = Math.min(...xs);
    const xMax = Math.max(...xs);
    const yMin = Math.min(...ys);
    const yMax = Math.max(...ys);
    const xRange = Math.max(1, xMax - xMin);
    const yRange = Math.max(1, yMax - yMin);

    return {
      xMin: Math.max(0, xMin - xRange * 0.08),
      xMax: xMax + xRange * 0.08,
      yMin: Math.max(0, yMin - yRange * 0.08),
      yMax: yMax + yRange * 0.08,
    };
  }, [scatter.points]);

  useEffect(() => {
    if (!chartRef.current || !scatter.points.length) return undefined;
    const chart = echarts.init(chartRef.current);
    const option = {
      animation: !exportMode,
      tooltip: {
        trigger: "item",
        formatter: (params) => {
          const p = params.data;
          return `<b>${p.name}</b><br/>Publications: ${formatBenchmarkChartValue(
            p.value[0],
            0
          )}<br/>Citations / publication: ${formatBenchmarkChartValue(
            p.value[1],
            1
          )}<br/>H-index: ${formatBenchmarkChartValue(p.value[2], 0)}`;
        },
      },
      grid: {
        left: 92,
        right: 40,
        top: 48,
        bottom: 78,
      },
      xAxis: {
        type: "value",
        name: "Scholarly output (publications)",
        nameLocation: "middle",
        nameGap: 40,
        nameTextStyle: { fontSize: 12 },
        axisLabel: { margin: 10, formatter: (v) => formatBenchmarkChartValue(v, 0) },
        min: paddedDomain?.xMin,
        max: paddedDomain?.xMax,
        z: 1,
        scale: true,
        splitLine: { show: true, lineStyle: { opacity: 0.2 } },
      },
      yAxis: {
        type: "value",
        name: "Citations / publication",
        nameLocation: "middle",
        nameGap: 52,
        nameTextStyle: { fontSize: 12 },
        axisLabel: { margin: 8, formatter: (v) => formatBenchmarkChartValue(v, 1) },
        min: paddedDomain?.yMin,
        max: paddedDomain?.yMax,
        z: 1,
        scale: true,
        splitLine: { show: true, lineStyle: { opacity: 0.2 } },
      },
      series: [
        {
          type: "scatter",
          z: 4,
          zlevel: 1,
          clip: false,
          data: scatter.points.map((p) => ({
            name: p.name,
            institution_id: p.institution_id,
            value: [p.x, p.y, p.size],
            itemStyle: {
              color: colorMap[p.institution_id],
              opacity: 0.55,
              borderColor: colorMap[p.institution_id],
              borderWidth: 2,
            },
          })),
          symbolSize: (val) =>
            scaleBubble(val[2], scatter.domain.sMin, scatter.domain.sMax),
          emphasis: {
            scale: true,
            itemStyle: {
              shadowBlur: 10,
              shadowColor: "rgba(0,0,0,0.2)",
            },
          },
        },
      ],
    };
    chart.setOption(option);
    const onResize = () => chart.resize();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      chart.dispose();
    };
  }, [scatter, colorMap, paddedDomain, exportMode]);

  return (
    <section
      className={styles.card}
      aria-labelledby="benchmark-scatter-title"
      style={
        exportMode
          ? { height: "100%", display: "flex", flexDirection: "column" }
          : undefined
      }
    >
      <h3
        id="benchmark-scatter-title"
        className={styles.title}
        data-report-strip="true"
      >
        Output vs. impact
      </h3>
      <p className={styles.subtitle} data-report-strip="true">
        Scholarly output (x) vs. citations per publication (y) · bubble size =
        h-index
      </p>
      {!scatter.points.length ? (
        <div className={styles.empty}>Not enough data to render the scatter chart.</div>
      ) : (
        <div
          ref={chartRef}
          className={styles.chart}
          style={exportMode ? { flex: 1, minHeight: 0, height: "auto" } : undefined}
          role="img"
          aria-label="Scatter plot of institution publication and citation averages with faculty-sized bubbles"
        />
      )}
    </section>
  );
};

export default BenchmarkScatterChart;
